import type React from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@xing-com/button';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { SimilarJobsAndRecos } from '@xing-com/crate-jobs-components-similar-jobs-and-recos';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useGoneStatusCode, useHost } from '@xing-com/crate-xinglet';
import {
  IllustrationEmptyStateSuitcase,
  IllustrationStickerSearch,
} from '@xing-com/illustrations';

import { StyledEmptyStateLayout } from './job-not-available.styles';

type Props = {
  geoFenced?: boolean;
  jobId?: string;
};

export const JobNotAvailable: React.FC<Props> = ({ geoFenced, jobId }) => {
  const { formatMessage } = useIntl();
  const trackNwt = useJobsNwtTracking();
  const host = useHost();

  // Enable custom error pages
  if (host.isServer) {
    host.setHeader('x-xing-customerrorpage', 'true');
  }

  // Set the status code to 410
  useGoneStatusCode({ isActive: host.isServer });

  useEffect(() => {
    trackNwt({
      eventSchema: 'basic',
      event: 'viewed_screen',
      page: 'jobs/empty_state_job_expired',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StyledEmptyStateLayout
        headline={formatMessage({
          id: geoFenced
            ? 'JOBS_JOB_NOT_AVAILABLE_HEADLINE_FENCED'
            : 'JOBS_JOB_NOT_AVAILABLE_HEADLINE',
        })}
        subline={formatMessage({
          id: geoFenced
            ? 'JOBS_JOB_NOT_AVAILABLE_SUBLINE_FENCED'
            : 'JOBS_JOB_NOT_AVAILABLE_SUBLINE',
        })}
        image={
          geoFenced ? (
            <IllustrationStickerSearch size="large" />
          ) : (
            <IllustrationEmptyStateSuitcase size="large" />
          )
        }
        button={
          <Button
            variant={geoFenced ? 'secondary' : 'tertiary'}
            size="medium"
            to={XING_JOBS_SEARCH_KI_PATH}
          >
            <FormattedMessage
              id={
                geoFenced
                  ? 'JOBS_JOB_NOT_AVAILABLE_CTA_FENCED'
                  : 'JOBS_JOB_NOT_AVAILABLE_CTA'
              }
            />
          </Button>
        }
      />
      <SimilarJobsAndRecos jobId={jobId} />
    </>
  );
};
